import { useEffect, useState } from 'react';
import { collection, getDocs, limit, orderBy, query } from 'firebase/firestore';
import { db } from '../../../firebase';
import type { FeedbackData, FeedbackState, FeedbackStats } from '../../../types/feedback';

export function useFeedbackData() {
  const [feedbackStates, setFeedbackStates] = useState<FeedbackState[]>([]);
  const [feedbackSubmissions, setFeedbackSubmissions] = useState<FeedbackData[]>([]);
  const [stats, setStats] = useState<FeedbackStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch user feedback states
        const statesQuery = query(collection(db, 'user_feedback_state'), orderBy('updatedAt', 'desc'), limit(500));
        const statesSnapshot = await getDocs(statesQuery);
        const statesData: FeedbackState[] = statesSnapshot.docs.map(
          (doc) =>
            ({
              id: doc.id,
              ...doc.data(),
            } as FeedbackState)
        );

        // Fetch feedback submissions
        const submissionsQuery = query(collection(db, 'user_feedback'), orderBy('createdAt', 'desc'), limit(100));
        const submissionsSnapshot = await getDocs(submissionsQuery);
        const submissionsData: FeedbackData[] = submissionsSnapshot.docs.map(
          (doc) =>
            ({
              id: doc.id,
              ...doc.data(),
            } as FeedbackData)
        );

        // Create a map of userId to their feedback submissions
        const feedbackByUser = submissionsData.reduce((acc, feedback) => {
          if (!acc[feedback.userId]) {
            acc[feedback.userId] = [];
          }
          acc[feedback.userId].push(feedback);
          return acc;
        }, {} as Record<string, FeedbackData[]>);

        // Attach user feedback submissions to their corresponding state
        const statesWithFeedback = statesData.map((state) => ({
          ...state,
          userFeedbackSubmissions: feedbackByUser[state.id] || [],
        }));

        // Calculate statistics
        const totalOptedOut = statesData.filter((state) => state.hasOptedOut).length;
        const totalActiveUsers = statesData.filter((state) => {
          const actionCounts = state.actionCounts || {};
          const totalUserActions = Object.values(actionCounts).reduce((sum, count) => sum + count, 0);
          return totalUserActions > 0;
        }).length;

        const totalActions = statesData.reduce((sum, state) => {
          return sum + Object.values(state.actionCounts || {}).reduce((actionSum, count) => actionSum + count, 0);
        }, 0);

        const feedbackBySource: Record<string, number> = {};
        const feedbackByFlow: Record<string, number> = {};

        submissionsData.forEach((feedback) => {
          if (feedback.source) {
            feedbackBySource[feedback.source] = (feedbackBySource[feedback.source] || 0) + 1;
          }
          if (feedback.flow) {
            feedbackByFlow[feedback.flow] = (feedbackByFlow[feedback.flow] || 0) + 1;
          }
        });

        const calculatedStats: FeedbackStats = {
          totalFeedbackSubmissions: submissionsData.length,
          totalActiveUsers,
          totalOptedOutUsers: totalOptedOut,
          averageActionsPerUser: totalActiveUsers > 0 ? totalActions / totalActiveUsers : 0,
          feedbackBySource,
          feedbackByFlow,
        };

        setFeedbackStates(statesWithFeedback);
        setFeedbackSubmissions(submissionsData);
        setStats(calculatedStats);
      } catch (err) {
        console.error('Error fetching feedback data:', err);
        setError('Failed to load feedback data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return {
    feedbackStates,
    feedbackSubmissions,
    stats,
    loading,
    error,
  };
}
